/**
 * @description Hook utilizado para manipular setores
 * @author Diego Sabino Amorim de Araújo
 * @file useSetorService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import { useDispatch } from 'react-redux'
import actionTypes from 'utils/actionTypes'
import { useFetch } from './useFetch'

export const useSetorService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarSetorAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/orgao/regional/divisao/setores/${id}`)
      return response.data.setor
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSetores = async (setSetores, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/orgao/regional/divisao/setores/completos`)
      setSetores(response.data.setores)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSetoresAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/orgao/regional/divisao/setores/completos`)
      return response.data.setores
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSetoresExternos = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/orgao/regional/divisao/setores/externos`)
      return response.data.setores
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSetoresAutocadastroAsync = async (token, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/auth/setores/${token}`, token)
      return response.data.setores
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSetoresGlobal = async (setProcessando) => {
    if (setProcessando) setProcessando(true)
    const response = await fetch.get(`/orgao/regional/divisao/setores/completos`)
    dispatch({
      type: actionTypes.OPCOES_SETORES,
      setores: response.data.setores,
    })
    if (setProcessando) setProcessando(false)
  }

  const listarSetoresDeDivisaoGlobal = async (idDivisao, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`orgao/regional/divisoes/${idDivisao}/setores/`)
      dispatch({
        type: actionTypes.FETCH_SETORES_DIVISAO,
        setores: response.data.setoresDivisao,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSetoresNecroterioGlobal = async () => {
    const response = await fetch.get(`/orgao/regional/divisao/setores/necroterio/`)
    dispatch({
      type: actionTypes.SET_NECROTERIOS,
      necroterios: response.data.necroterios,
    })
  }

  const listarSetoresNecroterioAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/orgao/regional/divisao/setores/necroterio/`)
      return response.data.necroterios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarSetor = async (dados, idDivisao, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const novaSetor = {
      nome: dados.nome,
      sigla: dados.sigla,
      descricao: dados.descricao,
      divisao: idDivisao,
      telefone: dados.telefone,
      endereco: dados.endereco,
      email: dados.email,
    }
    await fetch.postOpen('/orgao/regional/divisao/setores/', novaSetor)
    if (setProcessando) setProcessando(false)
  }

  const editarSetor = async (id, dados, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const divisao = {
      id: dados.id,
      nome: dados.nome,
      sigla: dados.sigla,
      descricao: dados.descricao,
      divisao: dados.divisao,
      telefone: dados.telefone,
      endereco: dados.endereco,
      email: dados.email,
    }
    await fetch.putOpen(`/orgao/regional/divisao/setores/${id}/`, divisao)
    if (setProcessando) setProcessando(false)
  }

  const deletarSetor = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`/orgao/regional/divisao/setores/${id}/`)
    if (setProcessando) setProcessando(false)
  }

  return {
    listarSetores,
    listarSetoresGlobal,
    listarSetoresAsync,
    listarSetoresExternos,
    listarSetoresAutocadastroAsync,
    listarSetoresNecroterioGlobal,
    listarSetoresNecroterioAsync,
    listarSetoresDeDivisaoGlobal,
    listarSetorAsync,
    deletarSetor,
    cadastrarSetor,
    editarSetor,
  }
}
